import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Send, Paperclip, X, FileText, Image as ImageIcon, Users } from "lucide-react";
import { toast } from "sonner";

const ROLE_BADGE = {
  admin: "bg-blue-50 text-blue-800 border-blue-200",
  employee: "bg-zinc-100 text-zinc-700 border-zinc-300",
};

export default function Chat() {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/chat/messages");
    setMessages(data);
    await api.post("/chat/mark-read");
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !file) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("text", text);
      if (file) fd.append("file", file);
      await api.post("/chat/messages", fd);
      setText("");
      setFile(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send");
    } finally { setSending(false); }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png", "application/pdf"].includes(f.type)) {
      toast.error("Only JPG, PNG and PDF allowed");
      return;
    }
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10 MB");
      return;
    }
    setFile(f);
    e.target.value = "";
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="chat-page">
      <header className="border-b border-zinc-200 page-px py-6 sm:py-8 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-uppercase mb-2">Workspace</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter flex items-center gap-3" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>
            <Users size={28} className="text-[#002FA7]" /> Team Chat
          </h1>
          <p className="text-xs text-zinc-500 mt-1 uppercase tracking-wider">Everyone · Admin + Employees</p>
        </div>
      </header>

      <div ref={scrollRef} className="flex-1 overflow-y-auto page-px py-6 space-y-4 bg-zinc-50/50" data-testid="chat-messages">
        {messages.length === 0 && (
          <div className="text-center text-sm text-zinc-500 py-12">No messages yet. Say hello!</div>
        )}
        {messages.map((m) => {
          const mine = m.author_id === user?.id;
          const isImage = (m.attachment_type || "").startsWith("image/");
          const isPdf = m.attachment_type === "application/pdf";
          return (
            <div key={m.id} className={`flex gap-3 ${mine ? "flex-row-reverse" : ""}`} data-testid={`chat-msg-${m.id}`}>
              <div className={`w-8 h-8 flex items-center justify-center text-[10px] font-bold uppercase shrink-0 ${
                mine ? "bg-ikb text-white" : (m.author_role === "admin" ? "bg-blue-700 text-white" : "bg-zinc-900 text-white")
              }`}>{m.author_name?.[0] || "?"}</div>

              <div className={`max-w-[78%] ${mine ? "items-end" : ""} flex flex-col`}>
                <div className={`flex items-center gap-2 mb-1 text-[10px] uppercase tracking-wider ${mine ? "flex-row-reverse" : ""}`}>
                  <span className="font-semibold text-zinc-700">{m.author_name}</span>
                  <span className={`px-1.5 py-0.5 border ${ROLE_BADGE[m.author_role] || ROLE_BADGE.employee}`}>{m.author_role}</span>
                  <span className="text-zinc-400 font-mono normal-case tracking-normal">{new Date(m.created_at).toLocaleString([], { hour: "2-digit", minute: "2-digit", month: "short", day: "numeric" })}</span>
                </div>

                <div className={`inline-block px-3 py-2 border ${
                  mine ? "bg-[#EAF0FF] border-[#B6C8F1]" : "bg-white border-zinc-200"
                }`}>
                  {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                  {isImage && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}${m.attachment_url}`} target="_blank" rel="noreferrer" className="block mt-2">
                      <img src={`${process.env.REACT_APP_BACKEND_URL}${m.attachment_url}`} alt={m.attachment_filename} className="max-w-xs max-h-60 border border-zinc-200" />
                    </a>
                  )}
                  {isPdf && (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${m.attachment_url}`}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 flex items-center gap-2 text-xs border border-zinc-200 px-2 py-1.5 hover:bg-zinc-50"
                    >
                      <FileText size={12} className="text-red-600" />
                      <span className="truncate">{m.attachment_filename}</span>
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={send} className="border-t border-zinc-200 page-px py-3 bg-white">
        {file && (
          <div className="mb-2 flex items-center gap-2 border border-zinc-200 px-2 py-1.5 bg-zinc-50 max-w-md">
            {file.type.startsWith("image/") ? <ImageIcon size={12} className="text-zinc-500" /> : <FileText size={12} className="text-red-600" />}
            <span className="text-xs truncate flex-1">{file.name}</span>
            <span className="text-[10px] font-mono text-zinc-400">{(file.size / 1024).toFixed(0)} KB</span>
            <button type="button" onClick={() => setFile(null)} className="text-zinc-400 hover:text-red-600"><X size={12} /></button>
          </div>
        )}
        <div className="flex gap-2 items-stretch">
          <label className="flex items-center justify-center px-3 border border-zinc-300 hover:bg-zinc-50 cursor-pointer" data-testid="chat-attach" title="Attach JPG or PDF">
            <Paperclip size={14} />
            <input type="file" accept="image/jpeg,image/png,application/pdf" onChange={onPickFile} className="hidden" />
          </label>
          <input
            data-testid="chat-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type a message…"
            className="flex-1 border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-[#002FA7]"
          />
          <button
            type="submit"
            disabled={sending || (!text.trim() && !file)}
            data-testid="chat-send"
            className="bg-ikb text-white px-4 py-2 text-sm font-medium flex items-center gap-2 hover:bg-[#00227A] disabled:opacity-50"
          >
            <Send size={14} />
          </button>
        </div>
      </form>
    </div>
  );
}
